/**
 * <Toolbar> — filter / search / view-control region between page header
 * and main content (e.g. table or grid).
 *
 * Provides a `start` slot for filter chips / search and an `end` slot for
 * view controls / density toggle / actions, with consistent spacing and
 * alignment. Wraps to two lines on narrow viewports.
 *
 * Spec: docs/design-system/03-spacing-layout.md.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface ToolbarProps extends HTMLAttributes<HTMLDivElement> {
  /** Left side: search, filter chips. */
  start?: ReactNode;
  /** Right side: view selector, density toggle, primary actions. */
  end?: ReactNode;
  /** Render directly into Toolbar without using slots. */
  children?: ReactNode;
}

export const Toolbar = forwardRef<HTMLDivElement, ToolbarProps>(function Toolbar(
  { start, end, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('flex flex-wrap items-center justify-between gap-3 py-2', className)}
      {...rest}
    >
      {start !== undefined || end !== undefined ? (
        <>
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">{start}</div>
          {end !== undefined && (
            <div className="flex items-center gap-2 flex-shrink-0">{end}</div>
          )}
        </>
      ) : (
        children
      )}
    </div>
  );
});

Toolbar.displayName = 'Toolbar';

export default Toolbar;
